<template>
    <div v-if="estate" class="page-temp">
        <div class="estate">
            <div class="estate__header">
                <div v-if="imagePreview" class="estate__header__hero" :style="{ backgroundImage: `url(${ imagePreview })` }">
                    <button class="estate__header__hero__btn" @click="modal.show = true">
                        <icon-images size="medium" />
                    </button>
                </div>

                <div class="estate__header__heading">
                    <h1 class="estate__header__heading__title">{{ estate.border_names }}</h1>
                </div>
            </div>

            <h2 class="estate__address">
                {{ `${ estate.street_address }
                    ${
                        (
                            propFloorType
                                ? `(${ propFloorType + (!(/[a-zA-Z]/).test(propFloorType) ? ` ${ $t('stairs_shorthand') }` : '') })`
                                : ''
                        )
                    }`
                }}
            </h2>

            <div class="estate__meta">
                <ul class="estate__meta__list">
                    <li
                        v-for="(item, index) in metaListLeft"
                        :key="index"
                        class="estate__meta__list__item"
                    >
                        <span v-if="index === 0" class="estate__meta__list__item__icon"><IconHouse size="medium" /></span>
                        <span v-if="item.property_label" class="estate__meta__list__item__label">{{ `${ item.property_label }: ` }}</span>
                        <span v-if="item.property_value" class="estate__meta__list__item__value">{{ item.property_value }}</span>
                    </li>
                </ul>

                <ul class="estate__meta__list">
                    <li
                        v-if="estateDescription"
                        class="estate__meta__list__item estate__meta__list__item--space"
                        v-html="estateDescription"
                    ></li>
                    <li
                        v-for="(item, index) in metaListRight"
                        :key="index"
                        class="estate__meta__list__item estate__meta__list__item--space"
                    >
                        <span v-if="item.property_label" class="estate__meta__list__item__label estate__meta__list__item__label--block">
                            {{ `${ item.property_label }: ` }}
                        </span>
                        <span v-if="item.property_value" class="estate__meta__list__item__value">{{ item.property_value }}</span>
                    </li>
                </ul>
            </div>

            <div class="estate__footer">
                <span class="estate__footer__published" v-if="estate.publish_date">{{ `${ $t('published') }  ${ estate.publish_date }` }}</span>
                <span class="estate__footer__last-updated" v-if="lastUpdated">{{ `${ $t('last_updated') }  ${ lastUpdated }` }}</span>
            </div>
        </div>

        <modal
            v-if="modal.show"
            :show="modal.show"
            :transparent="true"
            :closeBtn="true"
            @close="modal.close"
        >
            <carousel v-if="estate.attachment_files.length" :items-to-show="1">
                <slide
                    v-for="(slide, index) in estate.attachment_files"
                    :key="index"
                >
                    <div class="carousel__wrapper" :style="{ backgroundImage: `url(${ slide.url })` }"></div>
                    <p v-if="slide.description" class="carousel__txt">{{ slide.description }}</p>
                </slide>

                <template #addons>
                    <navigation />
                    <pagination />
                </template>
            </carousel>
        </modal>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, reactive, ref, watch, onMounted } from 'vue'
    import { useStore }                  from 'vuex'
    import { useRoute, useRouter }       from 'vue-router'
    import { useI18n }                   from 'vue-i18n'
    import { isNumeric, currentLang }    from '@/helpers'
    import { GoogleMap, Circle }         from 'vue3-google-map'
    import { Carousel, Slide, Pagination, Navigation } from 'vue3-carousel'
    import globalStoreMutations          from '@constans/store/global/globalStoreMutations'
    import estateStoreActions            from '@constans/store/estate/estateStoreActions'
    import estateStoreGetters            from '@constans/store/estate/estateStoreGetters'
    import authStoreGetters              from '@constans/store/auth/authStoreGetters'
    import userStoreGetters              from '@constans/store/user/userStoreGetters'
    import useFormatCurrency             from '@composables/useFormatCurrency'
    import modal                         from '@components/modal.vue'
    import IconHouse                     from '@components/icons/IconHouse.vue'
    import iconStar                      from '@components/icons/iconStar.vue'
    import iconPin                       from '@components/icons/iconPin.vue'
    import iconShare                     from '@components/icons/iconShare.vue'
    import iconImages                    from '@components/icons/iconImages.vue'
    import 'vue3-carousel/dist/carousel.css'

    export default defineComponent({
        components: {
            GoogleMap,
            Circle,
            Carousel,
            Slide,
            Pagination,
            Navigation,
            modal,
            IconHouse,
            iconStar,
            iconPin,
            iconShare,
            iconImages
        },
        setup() {
            const router = useRouter()
            const route  = useRoute()
            const store  = useStore()
            const { t, locale }  = useI18n()

            const estateCoords = ref<{lat: number|null, lng: number|null}>({
                lat: null,
                lng: null
            })
            const mapCircles =  ref<any>({})
            const locations = ref<any>({
                address: {
                    center: {
                        lat: null,
                        lng: null
                    },
                    raduis: 5
                }
            })

            const me          = computed(() => store.getters[`user/${ userStoreGetters.GET_ME }`])
            const estate = computed(() => store.getters[`estate/${ estateStoreGetters.GET_SINGLE_ESTATE }`])
            const imagePreview = computed(() => estate.value.attachment_files.length ? estate.value.attachment_files[0].url : null)
            
            const propFloorType = computed(() => estate.value.floor_type && estate.value.floor_type.id > 0 ? estate.value.floor_type.name[locale.value] : null)
            const propFurnishing = computed(() => estate.value.furniture_type && estate.value.furniture_type.id > 0 ? estate.value.furniture_type.name[locale.value] : null)
            const propExtension = computed(() => estate.value.extension_type && estate.value.extension_type.id > 0 ? estate.value.extension_type.name[locale.value] : null)
            const propKitchen = computed(() => estate.value.kitchen_type && estate.value.kitchen_type.id > 0 ? estate.value.kitchen_type.name[locale.value] : null)
            const propBalcony = computed(() => estate.value.balcony_type && estate.value.balcony_type.id > 0 ? estate.value.balcony_type.name[locale.value] : null)
            const estateDescription = computed(() => estate.value.description.replace(/(?:\r\n|\r|\n)/g, '<br>'))


            const propResidenceType = computed(() => {

                if (!estate.value.residence_type) {
                    return null
                }

                if (estate.value.residence_type.id == 2 || estate.value.residence_type.id == 3) {
                    return locale.value == 'sv' ? 'Lägenhet' : 'Apartment'
                }

                return estate.value.residence_type.name[locale.value];
            })


            const propHasElevator = computed(() => store.getters[`estate/${ estateStoreGetters.GET_MAPPED_PROP }`]({
                type: 'checkbox',
                key: 'ConfigureEstatePropertyEquipmentTypes',
                id: 10
            }))
            
            const propReqs = computed(() => store.getters[`estate/${ estateStoreGetters.GET_MAPPED_PROP }`]({
                type: 'checkbox',
                key: 'ConfigureEstatePropertyRequisitesTypes'
            }))
            const propIncludes = computed(() => store.getters[`estate/${ estateStoreGetters.GET_MAPPED_PROP }`]({
                type: 'checkbox',
                key: 'ConfigureEstatePropertyIncludeRentTypes'
            }))
            const propEquipments = computed(() => store.getters[`estate/${ estateStoreGetters.GET_MAPPED_PROP }`]({
                type: 'checkbox',
                key: 'ConfigureEstatePropertyEquipmentTypes'
            }))
            const lastUpdated = computed(() => store.getters[`estate/${ estateStoreGetters.GET_ESTATE_LAST_UPDATED }`])

            const modal = reactive({
                show: false,
                close: () => modal.show = !modal.show
            })

            const metaListLeft = computed(() => {
                
                const price = computed(() => {
                    if (me.value && estate.value.owner_id == me.value.id) {
                        return useFormatCurrency(estate.value.monthly_rent, 'SEK').price
                    } else {
                        return useFormatCurrency(estate.value.advertised_rent, 'SEK').price
                    }
                })

                let period = estate.value.available_from ? estate.value.available_from : t('now');
                period += ' - ';
                period += estate.value.available_until ? estate.value.available_until : t('until_further_notice');

                let leftColValues = [
                {
                    property_label: '',
                    property_value: propResidenceType.value
                },
                {
                    property_label: t('object_number'),
                    property_value: estate.value.object_number
                },
                {
                    property_label: t('room'),
                    property_value: estate.value.rooms
                },
                {
                    property_label: t('bedrooms'),
                    property_value: estate.value.bedrooms
                },
                {
                    property_label: t('size_sqm'),
                    property_value: estate.value.sqm
                }];

                if (propHasElevator.value) {
                    leftColValues.push({
                        property_label: t('elevator'),
                        property_value: propHasElevator.value ? t('yes') : t('no')
                    })
                }

                leftColValues.push({
                    property_label: t('rent'),
                    property_value: `${ price.value }`
                });

                leftColValues.push({
                    property_label: t('furnishing'),
                    property_value: propFurnishing.value
                })

                leftColValues.push({
                    property_label: t('kitchen_type'),
                    property_value: propKitchen.value ? propKitchen.value : t('no')
                })
                leftColValues.push({
                    property_label: t('balcony'),
                    property_value: propBalcony.value ? propBalcony.value : t('no')
                })
                leftColValues.push({
                    property_label: t('rental_period'),
                    property_value: period
                })
                leftColValues.push({
                    property_label: t('extension_possibility'),
                    property_value: propExtension.value ? propExtension.value : t('no')
                })

                return leftColValues
            })

            const metaListRight = computed(() => {
                let rightColValues = []

                if (estate.value && estate.value.closest_station) {
                    rightColValues.push({
                        property_label: t('closest_area'),
                        property_value: estate.value.closest_station
                    })
                }

                if (propIncludes.value) {
                    rightColValues.push({
                        property_label: t('included'),
                        property_value: propIncludes.value
                    })
                }

                if (propEquipments.value) {
                    rightColValues.push({
                        property_label: t('equipment'),
                        property_value: propEquipments.value
                    })
                }

                return rightColValues
            })

            const hasAccesToken = computed(() => store.getters[`auth/${ authStoreGetters.GET_ACCESS_TOKEN }`])

            const showInterest = () => {
                if (hasAccesToken.value) {
                    if (currentLang == 'en') {
                        document.location.href = `/en/show-interest/${route.params.estateId}`
                    } else {
                        document.location.href = `/sv/visa-intresse/${route.params.estateId}`
                    }
                } else {
                    if (confirm(t('login_to_show_interest'))) {
                        router.push({ name: 'login' })
                    }
                }
            }

            watch(estate, () => {
                estateCoords.value = {
                    lat: estate.value.lat,
                    lng: estate.value.lng
                }

                locations.value.address.center = estateCoords

                for (const key in locations.value) {
                    mapCircles.value[key] = {
                        center: locations.value[key].center,
                        radius: Math.sqrt(locations.value[key].raduis) * 100,
                        strokeColor: '#5d8089',
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: '#5d8089',
                        fillOpacity: 0.35,
                    }
                }
            })

            onMounted(() => {
                store.commit(`global/${ globalStoreMutations.UPDATE_LOADER }`, true)

                const endpoint = isNumeric((route.params.estateId as string))
                    ? `estate/${ estateStoreActions.GET_ESTATE }`
                    : `estate/${ estateStoreActions.GET_ESTATE_FROM_UUID }`

                store.dispatch(endpoint, route.params.estateId).then(OK => {
                    store.commit(`global/${ globalStoreMutations.UPDATE_LOADER }`, false)
                    
                    if (!OK) {
                        if (document.location.href.indexOf('/sv/') > -1) {
                            document.location.href = 'sv/bostaden-ar-inte-tillganglig';
                        } else {
                            document.location.href = 'en/estate-not-available';
                        }
                    }
                })
            })

            return {
                estate,
                imagePreview,
                metaListLeft,
                metaListRight,
                lastUpdated,
                modal,
                estateCoords,
                mapCircles,
                propResidenceType,
                estateDescription,
                propFloorType,
                locale,
                hasAccesToken,
                showInterest
            }
        }
    })
</script>
